import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { usePropertyStore } from 'src/stores/propertyStore';
import { BookingEngineServiceDTO } from 'src/services/api/data-contracts';
import { ReservationItem } from 'src/types';
import { PreCheckInReservationItem } from 'src/types/precheckin';
import { ServiceInventory, ReservationItemService } from 'src/types/common';

const apiService = Services.getService(Api);

export const useServiceStore = defineStore('serviceStore', {
  state: () => ({
    serviceInventoryByServiceId: {} as Record<number, ServiceInventory>,
    servicesPerRoomTypeAndRatePlan: {} as Record<
      string,
      BookingEngineServiceDTO[]
    >,
  }),
  getters: {},
  actions: {
    createKey(roomTypeId: number, ratePlanId: number) {
      const key = roomTypeId.toString() + '-' + ratePlanId.toString();
      return key;
    },
    clearCache() {
      this.serviceInventoryByServiceId = {};
      this.servicesPerRoomTypeAndRatePlan = {};
    },
    addResItemService(resX: ReservationItemService) {
      const obs: ServiceInventory =
        this.serviceInventoryByServiceId[resX.ServiceId];

      if (obs != null) {
        const resXFromService = obs.ReservationItems.filter(
          (u: ReservationItemService) => {
            return u.roomId == resX.roomId;
          },
        );

        if (resXFromService.length == 0) {
          obs.ReservationItems.push(resX);
        }
      }
    },
    removeResItemService(resX: ReservationItemService) {
      const obs: ServiceInventory =
        this.serviceInventoryByServiceId[resX.ServiceId];

      if (obs != null) {
        const resXFromService = obs.ReservationItems.filter(
          (u: ReservationItemService) => {
            return u.roomId == resX.roomId;
          },
        );

        if (resXFromService.length > 0) {
          const index = obs.ReservationItems.indexOf(resXFromService[0]);
          if (index > -1) {
            obs.ReservationItems.splice(index, 1);
          }
        }
      }
    },
    getAvailableServices(
      roomId: string,
      roomTypeId: number,
      ratePlanId: number,
    ): BookingEngineServiceDTO[] {
      const key = this.createKey(roomTypeId, ratePlanId);

      const servicesForRoomAndRate: BookingEngineServiceDTO[] =
        this.servicesPerRoomTypeAndRatePlan[key];

      const filteredServices = servicesForRoomAndRate.filter(
        (service: BookingEngineServiceDTO) => {
          const serviceId = service.ServiceId;
          let isAvailable = true;

          const obs: ServiceInventory =
            this.serviceInventoryByServiceId[serviceId];
          const availableInventory = obs.AvailableInventory;

          const roomAlreadyHasService = obs.ReservationItems.filter(
            (u: ReservationItemService) => {
              return u.roomId == roomId;
            },
          );

          isAvailable =
            availableInventory == null ||
            availableInventory > 0 ||
            roomAlreadyHasService.length > 0;

          return isAvailable;
        },
      );

      return filteredServices;
    },
    getReservationItemServices(reservationItem: PreCheckInReservationItem) {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      reservationItem.areServicesLoaded = false;

      if (propertyId) {
        const query = {
          propertyId: propertyId,
          reservationItemId: reservationItem.ReservationItemId,
          languageCode: 'en',
        };

        const cachePromise =
          apiService.serviceGetReservationItemServices(query);
        cachePromise.then((cacheResponse) => {
          this.initializeServiceInventory(
            cacheResponse.data.InventoryPerServiceId,
          );
          const key = this.createKey(
            reservationItem.RoomTypeId,
            reservationItem.RatePlanId,
          );
          reservationItem.hasSelectableServices =
            cacheResponse.data.Services.length > 0;

          this.servicesPerRoomTypeAndRatePlan[key] =
            cacheResponse.data.Services.map((t) => {
              return t;
            });

          reservationItem.areServicesLoaded = true;
        });
      }
    },

    getNewServices(reservationItem: ReservationItem) {
      const propertyStore = usePropertyStore();
      const propertyId = propertyStore.propertyId;

      reservationItem.areServicesLoaded = false;

      if (propertyId) {
        const query = {
          propertyId: propertyId,
          roomTypeId: reservationItem.roomTypeId,
          ratePlanId: reservationItem.ratePlanId,
          dateOfArrival: reservationItem.dateFrom,
          dateOfDeparture: reservationItem.dateTo,
          languageCode: 'en',
        };

        const cachePromise = apiService.serviceGet(query);
        cachePromise.then((cacheResponse) => {
          this.initializeServiceInventory(
            cacheResponse.data.InventoryPerServiceId,
          );
          const key = this.createKey(
            reservationItem.roomTypeId,
            reservationItem.ratePlanId,
          );
          reservationItem.hasSelectableServices =
            cacheResponse.data.Services.length > 0;

          this.servicesPerRoomTypeAndRatePlan[key] =
            cacheResponse.data.Services.map((t) => {
              return t;
            });

          reservationItem.areServicesLoaded = true;
        });
      }
    },
    initializeServiceInventory(
      serviceInventoryByServiceId: Record<number, number>,
    ) {
      for (const key in serviceInventoryByServiceId) {
        if (serviceInventoryByServiceId.hasOwnProperty(key)) {
          const serviceId = key;
          const inventory = serviceInventoryByServiceId[key];

          let obs: ServiceInventory =
            this.serviceInventoryByServiceId[serviceId];
          if (obs == null) {
            obs = new ServiceInventory();
            obs.ServiceId = Number.parseInt(serviceId);
            obs.StartingInventory = inventory;
            this.serviceInventoryByServiceId[serviceId] = obs;
          } else {
            obs.StartingInventory = inventory;
          }
        }
      }
    },
  },
});
